import { BUSINESS_OWNERSHIP } from 'enums/taxSurveyOptions';
import { ProgressTrackerGroup, TBusinessGoals } from './account';
import { EncryptedSSN, UserName } from './user';

export type DEKValuePair = string;

export type YTDCatchup = {
  income: number;
  categoryExpense: {
    [key: string]: number;
  };
  associatedWithBrokerage: boolean;
  updatedAt?: string;
  completed?: boolean;
};
export interface Address {
  street1?: string;
  street2?: string;
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
}

export enum BusinessStatusLabel {
  NEW = 'NEW',
  ARCHIVED = 'ARCHIVED',
  ACTIVE = 'ACTIVE',
}

export type BusinessStatus = {
  label: BusinessStatusLabel;
  updatedAt?: string;
  updatedBy?: string;
};

export enum BusinessPlan {
  FullSuite = 'FullSuite',
  TaxOnly = 'TaxOnly',
  BIB = 'BIB',
}

export interface Business {
  id: string;
  accountId: string;
  organizationId: string;
  name?: string;
  businessAddress?: Address;
  mailingAddress?: Address;
  workPhone?: string;
  entityType?: string;
  originalEntityType?: string;
  industryCode?: string;
  description?: string;
  ein?: string;
  scorp?: boolean;
  businessOwnership?: keyof typeof BUSINESS_OWNERSHIP;
  businessGoals?: TBusinessGoals[];
  statesOfOperation?: string[];
  suggested?: SuggestedCompanyName[];
  useExistingBank?: boolean;
  gusto?: Gusto;
  xero?: Xero;
  hurdlr?: Hurdlr;
  payroll: Payroll;
  payrollEnabled?: boolean;
  payrollRunNumber?: number;
  bankName?: string;
  routingNumber?: string;
  bankAccountNumber?: string;
  bankAccountType?: string;
  incorporationDate?: string;
  stateOfIncorporation?: string;
  hasBankAccount: boolean;
  disabledPayroll: boolean;
  incorporationRenewalDate?: string;
  incorporationStatus?: string;
  bankAccountNames?: string;
  hasEIN?: boolean;
  secretaryOfStateId?: string;
  bibHubspot?: BiBHubspot;
  businessStartYear?: number;
  signatureRequestTemplate?: string;
  payrollPlanStatus?: string;
  payrollPlanStatusUpdatedAt?: string;
  incorporationVerificationTicketId?: string;
  createdAt?: string;
  updatedAt?: string;
  updatedBy?: string;
  projectedGrossIncome?: number;
  ytdCatchup?: YTDCatchup;
  progress: ProgressTrackerGroup[];
  sElectionDate?: string;
  status?: BusinessStatusLabel;
  formationsPlan?: BusinessPlan;
}

interface SuggestedCompanyName {
  name?: string;
  isAvailable?: boolean;
}

interface BiBHubspot {
  ticketId: string;
  ticketStatus: string;
  actionDescription: string;
}

interface GustoCredentials {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
  createdAt: number;
  tokenType: string;
}

interface GustoUpcomingRequest {
  type: string;
  date?: Date;
  payrollUUID: string;
}

interface Gusto {
  companyUUID: string;
  companyName: string;
  credentialsUserId?: string;
  credentials?: GustoCredentials;
  lastUpdated?: Date;
  upcomingRequests?: GustoUpcomingRequest[];
  acceptedToSDate?: Date;
  onboardingStatus?: string;
  connectionLost?: boolean;
}

export interface Payroll {
  estimatedSalary: number;
  ytdOfficerWages: number;
  ytdNonOfficerWages: number;
  lyOfficerWages: number;
  lyNonOfficerWages: number;
}

interface BusinessOwner {
  id: string;
  businessId: string;
  name?: UserName;
  ssn: EncryptedSSN;
  dob?: string;
  email?: string;
  ownershipPercentage: number;
  proofOfIDs?: string[];
  createdAt?: Date;
  updatedAt?: Date;
  updatedBy?: string;
}

interface XeroBankSummary {
  openingBalance: number;
  cashReceived: number;
  cashSpent: number;
  closingBalance: number;
}

export interface XeroInsights {
  ytdExpense: number;
  ytdGrossProfit: number;
  ytdNetProfit: number;
  ytdTotalIncome: number;
  lyExpense: number;
  lyGrossProfit: number;
  lyNetProfit: number;
  lyTotalIncome: number;
  ytdWages: number;
  lyWages: number;
  ytdBankSummary: XeroBankSummary;
  lmBankSummary: XeroBankSummary;
  lmBankSummaryLastUpdatedAt?: Date;
}

interface XeroNonRetryal {
  nonRetryalCount: number;
  lastOccurredAt?: Date;
}

interface XeroOrganization {
  organizationId: string;
  shortCode: string;
  lastUpdatedAt?: Date;
}

interface XeroLastOrganization extends XeroOrganization {
  tenantId: string;
  tenantName: string;
}

interface XeroError {
  code: string;
  message: string;
  occurredAt: Date;
}

interface XeroCredentials {
  connectionId: string;
  accessToken: DEKValuePair;
  refreshToken: DEKValuePair;
  tenantId: string;
  tenantName: string;
  updatedAt?: Date;
}

interface Xero {
  credentials?: XeroCredentials;
  insights: XeroInsights;
  lastError: string;
  lastUpdatedAt?: Date;
  lastUpdatedAtTransactions?: Date;
  transactionsError: XeroError[];
  nonRetryableError: boolean;
  nonRetryal?: XeroNonRetryal;
  organization?: XeroOrganization;
  lastOrganization?: XeroLastOrganization;
}

interface HurdlrCredentials {
  accessToken: DEKValuePair;
  refreshToken: DEKValuePair;
  scope: string;
  role: 'user' | 'accountant';
  expiresAt: Date;
  updatedAt: Date;
}

interface HurdlrProfitLoss {
  beginDate: string;
  endDate: string;
  netIncome: number;
  income: number;
  expenses: number;
  grossProfit: number;
}

export interface HurdlrInsights {
  lyProfitLoss?: HurdlrProfitLoss;
  ytdProfitLoss?: HurdlrProfitLoss;
  lyOfficerWages?: number;
  ytdOfficerWages?: number;
  lmCashflow?: HurdlrCashflow;
  ytdCashflow?: HurdlrCashflow;
}

interface HurdlrCashflow {
  beginDate: string;
  endDate: string;
  netCashChange: number;
  beginCashBalance: number;
  endCashBalance: number;
}

interface HurdlrEntityUpdate {
  entity: string;
  lastUpdatedDate?: string;
  lastError?: string;
  lastErrorAt?: Date;
}

interface HurdlrBankConnectionStatus {
  connectionCount: number;
  errorConnectionCount: number;
}

interface Hurdlr {
  insights: HurdlrInsights;
  entityUpdates: Record<string, HurdlrEntityUpdate>;
  bankConnectionStatus: HurdlrBankConnectionStatus;
  invoicingBusinessId?: string;
  isConnected?: boolean;
}
